import React, { useRef } from 'react';
import { ChevronLeft, ChevronRight } from 'lucide-react';
import { motion } from 'framer-motion';

interface EducationalTopic {
  tag: string;
  title: string;
  focus: string;
  description: string;
}

const educationalTopics: EducationalTopic[] = [
  {
    tag: "Mood Disorders",
    title: "Depression & Low Mood",
    focus: "Comprehensive Assessment & Care",
    description: "Major depressive disorder affects emotional, cognitive, and physical functioning. Clinical psychiatric evaluation differentiates situational distress from clinical depression, guiding structured interventions including psychotherapy and medical management when appropriate."
  },
  {
    tag: "Anxiety Spectrum",
    title: "Anxiety & Panic Management",
    focus: "Cognitive Behavioral Strategies",
    description: "Persistent worry, social anxiety, and panic symptoms respond effectively to evidence-based medical guidance, cognitive restructuring, psychoeducation, and stress-reduction techniques tailored to individual needs."
  },
  {
    tag: "Bipolar Care",
    title: "Bipolar Spectrum Conditions",
    focus: "Mood Stabilization & Monitoring",
    description: "Bipolar disorder involves cyclical shifts between depressive episodes and elevated energy levels. Longitudinal psychiatric care with evidence-based mood stabilizing regimens and family psychoeducation supports emotional regulation."
  },
  {
    tag: "Specialized Care",
    title: "Women's Mental Health",
    focus: "Perinatal & Life Transitions",
    description: "Perinatal transitions, hormonal changes, and psychosocial stressors present distinct psychiatric considerations. Focused psychiatric care provides a confidential clinical setting addressing specific psychological and physiological needs."
  },
  {
    tag: "Therapeutic Guidance",
    title: "Psychotherapy & Counselling",
    focus: "Evidence-Based Interventions",
    description: "Psychotherapy offers a structured clinical framework to build emotional resilience, develop adaptive coping mechanisms, and navigate relationship challenges alongside medical psychiatric care when indicated."
  }
];

const EducationalCard: React.FC<EducationalTopic & { index: number }> = ({ tag, title, focus, description, index }) => (
  <motion.div
    initial={{ opacity: 0, y: 50 }}
    animate={{ opacity: 1, y: 0 }}
    transition={{ duration: 0.5, delay: index * 0.1 }}
    className='bg-white rounded-lg shadow-md p-6 flex flex-col justify-between w-80 h-96 transition-all duration-300 ease-in-out hover:shadow-xl hover:-translate-y-1'
  >
    <div>
      <div className="mb-3">
        <span className="inline-block bg-pink-100 text-pink-800 text-xs font-semibold px-2.5 py-1 rounded-full uppercase tracking-wider">
          {tag}
        </span>
      </div>
      <h3 className="text-xl font-bold text-gray-800 mb-1">{title}</h3>
      <p className="text-xs font-semibold text-pink-600 mb-3">{focus}</p>
      <motion.p
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ duration: 0.5, delay: index * 0.1 + 0.3 }}
        className="text-gray-600 text-sm leading-relaxed"
      >
        {description}
      </motion.p>
    </div>
    <div className="pt-3 border-t border-gray-100 flex items-center justify-between text-xs text-gray-500">
      <span>Evidence-Based Information</span>
      <span className="text-pink-600 font-medium">Dr. Bushra Zahoor</span>
    </div>
  </motion.div>
);

const Testimonials: React.FC = () => {
  const scrollRef = useRef<HTMLDivElement>(null);

  const scrollRight = () => {
    if (scrollRef.current) {
      scrollRef.current.scrollBy({ left: 400, behavior: 'smooth' });
    }
  };

  const scrollLeft = () => {
    if (scrollRef.current) {
      scrollRef.current.scrollBy({ left: -400, behavior: 'smooth' });
    }
  };

  return (
    <section id='testimonials' className='w-full py-24'>
      <div className="relative w-full max-w-screen-xl mx-auto py-12 px-4">
        <motion.h2
          initial={{ opacity: 0, y: -50 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5 }}
          className="text-3xl sm:text-4xl md:text-5xl mb-8 text-foreground font-fraunces-slab text-center"
        >
          Mental Health Insights & Care Principles
        </motion.h2>
        <div className="flex flex-col md:flex-row">
          {/* Left side content */}
          <motion.div
            initial={{ opacity: 0, x: -50 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ duration: 0.5, delay: 0.2 }}
            className="w-full md:w-1/2 mb-8 md:mb-0 pr-0 md:pr-8"
          >
            <div className="bg-white rounded-lg p-6 h-full flex flex-col justify-center items-center mt-8">
              <motion.img
                initial={{ scale: 0.8, opacity: 0 }}
                animate={{ scale: 1, opacity: 1 }}
                transition={{ duration: 0.5, delay: 0.4 }}
                src="/images/testimonials.jpg"
                alt="Clinical mental health care consultation"
                className="rounded-lg mb-4"
              />
              <motion.h3
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.5, delay: 0.6 }}
                className="text-2xl font-bold text-pink-800 mb-2"
              >
                Evidence-Based Clinical Care
              </motion.h3>
              <motion.p
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.5, delay: 0.8 }}
                className="text-pink-600 text-center"
              >
                Understanding mental health conditions empowers individuals and families to seek timely clinical support. Explore key medical insights on common psychiatric conditions and evidence-based care approaches.
              </motion.p>
            </div>
          </motion.div>

          {/* Right side educational topics */}
          <div className="w-full md:w-1/2 relative mt-8">
            <div
              ref={scrollRef}
              className="flex overflow-x-auto scrollbar-hide no-scrollbar snap-x snap-mandatory gap-6 pb-16 md:pb-8"
              style={{ scrollSnapType: 'x mandatory' }}
            >
              {educationalTopics.map((topic, index) => (
                <div key={index} className="snap-center flex-shrink-0">
                  <EducationalCard {...topic} index={index} />
                </div>
              ))}
            </div>
            <div className="absolute bottom-0 left-0 right-0 flex justify-between px-4 md:px-0 md:justify-center md:space-x-4">
              <motion.button
                whileHover={{ scale: 1.1 }}
                whileTap={{ scale: 0.9 }}
                onClick={scrollLeft}
                className="bg-white rounded-full p-3 shadow-md hover:bg-gray-100 transition-colors duration-200 focus:outline-none focus:ring-2 focus:ring-blue-500 z-10"
              >
                <ChevronLeft className="w-6 h-6 text-gray-600" />
              </motion.button>
              <motion.button
                whileHover={{ scale: 1.1 }}
                whileTap={{ scale: 0.9 }}
                onClick={scrollRight}
                className="bg-white rounded-full p-3 shadow-md hover:bg-gray-100 transition-colors duration-200 focus:outline-none focus:ring-2 focus:ring-blue-500 z-10"
              >
                <ChevronRight className="w-6 h-6 text-gray-600" />
              </motion.button>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default Testimonials;
